import { Suspense, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { Loader } from '@givebutter/primitives/loader';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { useMediaQuery } from '@givebutter/react-util';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Styled from 'assets/js/pages/account-settings/account-integrations/widgets-form/styled';
import { useAccountsWidgetForm } from 'assets/js/pages/account-settings/account-integrations/widgets-form/useAccountsWidgetForm';
import * as Form from 'components/form';
import { GivebutterFormProvider } from 'components/form/GivebutterFormProvider';
import * as WidgetForm from 'components/form/WidgetForm';
import { WIDGETS_DOCS_LINK } from 'constants/links';
import BubblePreview from 'images/widgets-preview-bubble.webp';
import DonationFormPreview from 'images/widgets-preview-donation-form.webp';
import SignupFormPreview from 'images/widgets-preview-signup-form.webp';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTheme } from 'styled-components';

export const AccountsWidgetForm = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoint.mobile})`);
    const {
        returnUrl,
        isEditing,
        globalDisabled,
        showConfig,
        isButton,
        isButtonTypeFloating,
        isSignupForm,
        isGivingForm,
        isGoalBarTypeProgressBar,
        isGoalBarThermometerType,
        isSignupFormTypePopup,
        isSignupFormNonStaticLayout,
        onSubmit,
        onClickSubmit,
        methods,
    } = useAccountsWidgetForm({ defaultValues: { widgetType: 'button' } });
    const {
        control,
        formState: { errors },
        path,
    } = methods;
    const buttonRadioRef = useRef(null);
    const donationFormRadioRef = useRef(null);
    const signupFormRadioRef = useRef(null);
    const { contactsWithoutName = false } = useFlags();

    return (
        <GivebutterFormProvider {...methods}>
            <form onSubmit={onSubmit}>
                <WidgetForm.Container>
                    {!isEditing && (
                        <>
                            <Label htmlFor="widget-type" asChild={true}>
                                <p className="!text-2xl font-medium">Widgets</p>
                            </Label>
                            <Styled.SdkInstructions className="text-base">
                                Select the Widget you'd like to configure for display on your
                                website.
                            </Styled.SdkInstructions>
                            <Form.UseFormRadioGroup
                                control={control}
                                name="widgetType"
                                id="widget-type"
                                className="gap-4"
                                variant="card"
                            >
                                <RadioGroup.Item
                                    ref={buttonRadioRef}
                                    disabled={globalDisabled}
                                    id="button-choice"
                                    value="button"
                                    className="flex flex-col gap-4"
                                >
                                    <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                                        />
                                    </RadioGroup.Indicator>
                                    <div className="flex flex-col gap-2">
                                        <Form.Label
                                            className="group-radix-state-checked:text-blue-500"
                                            name={path('widgetType')}
                                            htmlFor="button-choice"
                                        >
                                            Donation button
                                        </Form.Label>
                                        <p>Popup donation button for any part of your site.</p>
                                    </div>
                                    {!isMobile && (
                                        <Styled.WidgetTypeImageContainer>
                                            <img
                                                src={BubblePreview}
                                                alt="Donation button widget preview"
                                            />
                                        </Styled.WidgetTypeImageContainer>
                                    )}
                                </RadioGroup.Item>
                                <RadioGroup.Item
                                    ref={donationFormRadioRef}
                                    disabled={globalDisabled}
                                    id="giving-form-choice"
                                    value="giving-form"
                                    className="flex flex-col gap-4"
                                >
                                    <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                                        />
                                    </RadioGroup.Indicator>
                                    <div className="flex flex-col gap-2">
                                        <Form.Label
                                            name={path('widgetType')}
                                            htmlFor={'giving-form-choice'}
                                            className="group-radix-state-checked:text-blue-500"
                                        >
                                            Donation form
                                        </Form.Label>
                                        <p>Embedded donation form directly on your webpage.</p>
                                    </div>
                                    {!isMobile && (
                                        <Styled.WidgetTypeImageContainer>
                                            <img
                                                src={DonationFormPreview}
                                                alt="Donation form widget preview"
                                            />
                                        </Styled.WidgetTypeImageContainer>
                                    )}
                                </RadioGroup.Item>
                                <RadioGroup.Item
                                    ref={signupFormRadioRef}
                                    disabled={globalDisabled}
                                    id="signup-form-choice"
                                    value="signup-form"
                                    className="flex flex-col gap-4"
                                >
                                    <RadioGroup.Indicator className="absolute right-6 top-3 inline-block">
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            className="h-3 w-3 rounded-full bg-blue-500 p-0.5 text-white"
                                        />
                                    </RadioGroup.Indicator>
                                    <div className="flex flex-col gap-2">
                                        <Form.Label
                                            name={path('widgetType')}
                                            htmlFor={'signup-form-choice'}
                                            className="group-radix-state-checked:text-blue-500"
                                        >
                                            Signup form
                                        </Form.Label>
                                        <p>Grow your audience with embeddable signup forms.</p>
                                    </div>
                                    {!isMobile && (
                                        <Styled.WidgetTypeImageContainer>
                                            <img
                                                src={SignupFormPreview}
                                                alt="Signup form widget preview"
                                            />
                                        </Styled.WidgetTypeImageContainer>
                                    )}
                                </RadioGroup.Item>
                            </Form.UseFormRadioGroup>
                        </>
                    )}
                    <Suspense fallback={<Loader />}>
                        {showConfig && (
                            <div className={'grid grid-cols-2 gap-6'}>
                                <div className={'col-span-2 flex flex-col gap-4'}>
                                    <p className={'text-2xl font-medium'}>Configuration</p>
                                    <Styled.ConfigInstructions className="text-base">
                                        Any customizations made here will display on your page upon
                                        a refresh. Be sure to review your changes before clicking
                                        Publish. For advanced customizations, reference the{' '}
                                        <a
                                            href={WIDGETS_DOCS_LINK}
                                            target={'_blank'}
                                            rel="noreferrer"
                                        >
                                            docs
                                        </a>
                                        .
                                    </Styled.ConfigInstructions>
                                </div>
                                <WidgetForm.NameInput />

                                {isButton && (
                                    <>
                                        <WidgetForm.CampaignInput />
                                        <WidgetForm.ButtonTypeInput />
                                        {isButtonTypeFloating && <WidgetForm.PositionInput />}
                                        <WidgetForm.BackgroundColorInput />
                                        <WidgetForm.LabelInput />

                                        <WidgetForm.BorderWidthInput />
                                        <WidgetForm.BorderColorInput />
                                        <WidgetForm.BorderRadiusInput />
                                        <WidgetForm.LabelColorInput />
                                        {isButtonTypeFloating ? (
                                            <>
                                                <WidgetForm.IconInput />
                                                <WidgetForm.ShadowInput />
                                            </>
                                        ) : (
                                            <>
                                                <WidgetForm.ShadowInput />
                                                <WidgetForm.IconInput />
                                            </>
                                        )}
                                        <WidgetForm.Preview />
                                    </>
                                )}
                                {isGivingForm && (
                                    <>
                                        <WidgetForm.CampaignInput />
                                        <WidgetForm.DisplayGoalBarInput />
                                        <div />
                                        <div className={'col-span-2'}>
                                            <WidgetForm.Preview />
                                        </div>
                                    </>
                                )}
                                {isSignupForm && (
                                    <>
                                        <WidgetForm.CommunicationChannelInput />
                                        <div className={'col-span-2'}>
                                            <WidgetForm.SignupFormTypeInput />
                                        </div>
                                        {isSignupFormTypePopup && (
                                            <div className={'col-span-2'}>
                                                <WidgetForm.SignupFormOpenDelayInput />
                                            </div>
                                        )}
                                        <div className={'bold col-span-2'}>
                                            <p className={'text-xl font-medium'}>Content</p>
                                        </div>
                                        <WidgetForm.SignupFormTitleInput />

                                        <WidgetForm.SignupFormButtonTextInput />
                                        <div className={'col-span-2'}>
                                            <WidgetForm.SignupFormDescriptionInput />
                                        </div>
                                        {contactsWithoutName && (
                                            <>
                                                <div className={'bold col-span-2'}>
                                                    <p className={'text-xl font-medium'}>
                                                        Required Fields
                                                    </p>
                                                </div>
                                                <div className="col-span-1">
                                                    <WidgetForm.SignupRequiredFields />
                                                </div>
                                            </>
                                        )}
                                        <div className={'bold col-span-2'}>
                                            <p className={'text-xl font-medium'}>Styling</p>
                                        </div>
                                        <WidgetForm.LayoutInput />
                                        <WidgetForm.SignupFormBorderRadiusInput />
                                        <WidgetForm.ButtonBackgroundColorInput />
                                        <WidgetForm.ButtonTextColorInput />
                                        {isSignupFormNonStaticLayout && (
                                            <>
                                                <WidgetForm.ImageInput />
                                                <WidgetForm.SignupFormImageSizingInput />
                                            </>
                                        )}
                                        <div />

                                        <div className={'col-span-2'}>
                                            <WidgetForm.Preview />
                                        </div>
                                    </>
                                )}

                                {isGoalBarThermometerType && (
                                    <>
                                        <WidgetForm.GoalBarTypeInput />
                                        <WidgetForm.TextColorInput />
                                        <WidgetForm.ProgressBarColor />
                                        <WidgetForm.Preview />
                                    </>
                                )}

                                {isGoalBarTypeProgressBar && (
                                    <>
                                        <WidgetForm.GoalBarTypeInput />
                                        <WidgetForm.SizeInput />
                                        <WidgetForm.TextColorInput />
                                        <WidgetForm.ProgressBarColor />
                                        <WidgetForm.BackgroundColorInput />
                                        <WidgetForm.BorderWidthInput />
                                        <WidgetForm.BorderColorInput />
                                        <WidgetForm.BorderRadiusInput />
                                        <div>&nbsp;</div>
                                        <div className={'col-span-2'}>
                                            <WidgetForm.Preview />
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </Suspense>
                    {Object.keys(errors).length > 0 && (
                        <Styled.AlertNotification type="error" closable={true}>
                            Please fix form errors and try submitting again.
                        </Styled.AlertNotification>
                    )}
                    <div className={'flex justify-end gap-2'}>
                        <Link to={returnUrl}>
                            <Button variant="outline" color="neutral">
                                Cancel
                            </Button>
                        </Link>
                        {showConfig && (
                            <Form.SubmitButton
                                onClick={onClickSubmit}
                                disabled={globalDisabled}
                                type="submit"
                            >
                                {isEditing ? 'Publish' : 'Create Widget'}
                            </Form.SubmitButton>
                        )}
                    </div>
                </WidgetForm.Container>
            </form>
        </GivebutterFormProvider>
    );
};
